// src/pages/merchant-without-customer/createNoCustomerInvoice.js
import React, { useState, useEffect, useContext, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import axios from "axios";

// Assets
import addIcon from "../../assets/add_icon.png";
import saveIcon from "../../assets/save_icon.png";
import closeIcon from "../../assets/close_icon.png";
import previousIcon from "../../assets/previous_icon.png";
import nextIcon from "../../assets/next_icon.png";

// Styles
import classes from "../../components/Modal.module.css";

// Utils
import { DataContext } from "../../utils/DataContext";
import useLanguage from "../../utils/useLanguage";
import useRandomIcon from "../../utils/useRandomIcon";
import { sanitizeNumber } from "../../utils/sanitizeNumber";
import { onlyNums } from "../../utils/OnlyNums";
import { shouldCloseModal } from "../../utils/shouldCloseModal";
import { UserAuth } from "../../utils/AuthContext";

// Components
import ShippedContainerCard from "../../components/ShippedContainerCard";
import ErrorBox from "../../components/ErrorBox";
import Loader from "../../components/Loader";
import CustomInput from "../../components/CustomInput";
import Dropdown from "../../components/Dropdown";
import CustomButton from "../../components/CustomButton";
import PDFUploader from "../../components/PDFUploader";
import InformationBox from "../../components/InformationBox";
import HotDropdown from "../../components/HotDropdown";
import AreYouSureModal from "../../components/AreYouSureModal";

// Page sections
import AddNoCustomerContainerModal from "./AddNoCustomerContainerModal";
import PageWrapper from "../../components/PageWrapper";
import BackToDashboard from "../../components/BackToDashboard";
import CustomInputWithUnit from "../../components/CustomInputWithUnit";

const CreateNoCustomerInvoice = ({ setShowInputFields }) => {
  const [inputs, setInputs] = useState([]);

  const { t } = useTranslation("general");
  const { currentDirection } = useLanguage();
  const navigate = useNavigate();

  const [showAddShippedContainerModal, setShowAddShippedContainerModal] =
    useState(false);

  // Create container modal state
  const [showAreYouSure, setShowAreYouSure] = useState(false);

  // Create container modal
  const [areYouSureDelete, setAreYouSureDelete] = useState(false);
  const [containerToDelete, setContainerToDelete] = useState(null);

  const [currentContainer, setCurrentContainer] = useState(null);
  // Handle opening the modal for a specific container
  const openModal = (index) => {
    setCurrentContainer(index);
    setShowAddShippedContainerModal(true);
  };

  const [loadingModal, setLoadingModal] = useState(false);
  const {
    suppliers,
    getSuppliers,
    selectedSupplier,
    error,
    setError,
    setShippedContainers,
    handleSelectSupplier,
    subCompanies,
    selectedSubCompany,
    handleSelectSubCompany,
    getSubCompanies,
  } = useContext(DataContext);

  const { userData } = useContext(UserAuth);

  const getRandomIcon = useRandomIcon();

  const [invoiceDocs, setInvoiceDocs] = useState([]);
  const pdfUploaderRef = useRef(null);

  const [invoice, setInvoice] = useState({
    invoiceNumber: "",
    amountForSupplier: "",
    abstractAmountForCustomer: "",
    finalAmountForCustomer: "",
    date: new Date(),
    received: "",
    left: "",
    notes: "",
    currencyRate: 0,
  });

  const handleInvoiceInputChange = (e) => {
    const { name, value } = e.target;

    setError("");
    setInvoice((prev) => ({ ...prev, [name]: value }));
  };

  const handleInvoiceDateChange = (e) => {
    const { name, value } = e.target;

    setInvoice((prev) => ({ ...prev, [name]: new Date(value) }));
  };

  const handleAmountChange = (e) => {
    const { name, value } = e.target;

    setInvoice((prev) => {
      return {
        ...prev,
        [name]: value,
      };
    });
  };

  const handleChildUploadDocs = async (multiContainersInvoiceId) => {
    if (pdfUploaderRef.current) {
      await pdfUploaderRef.current.handleUploadDocs(multiContainersInvoiceId);
    }
  };

  const handleAttemptDeleteContainer = (e, index) => {
    e.stopPropagation();
    setContainerToDelete(index);
    setAreYouSureDelete(true);
  };

  const handleDeleteContainer = (containerIndex) => {
    const filteredInputs = inputs?.filter(
      (input, index) => index !== containerIndex,
    );

    setInputs(filteredInputs);
    setAreYouSureDelete(false);
    setContainerToDelete(null);
  };

  const handleAddRow = async () => {
    try {
      const token = userData?.token;
      const userId = parseInt(userData?.userId, 10);
      const organizationId = parseInt(
        localStorage.getItem("organizationId"),
        10,
      );

      if (!userId) {
        console.error("User ID not found in localStorage");
        return;
      }

      setLoadingModal(true);

      if (!invoice?.invoiceNumber) {
        setError("invalidInvoiceNumber");
        window.scroll(0, 0);
        return;
      }

      if (!selectedSubCompany?.id || selectedSubCompany?.id === "All") {
        setError("noCompany");
        window.scroll(0, 0);
        return;
      }
      if (!selectedSupplier?.id) {
        setError("noSupplier");
        window.scroll(0, 0);
        return;
      }

      // Prepare the payload
      const payload = {
        supplierId: selectedSupplier?.id,
        // customerId: selectedCustomer?.id,
        subCompanyId: selectedSubCompany?.id,
        createdBy: userId,
        ...invoice,
        amountForSupplier: sanitizeNumber(invoice?.amountForSupplier),
        abstractAmountForCustomer: sanitizeNumber(
          invoice?.abstractAmountForCustomer,
        ),
        finalAmountForCustomer: sanitizeNumber(invoice?.finalAmountForCustomer),
        containers: inputs,
        organizationId,
      };

      setError("");
      // Send the request to the backend
      const response = await axios.post(
        `${process.env.REACT_APP_URL}/createMultiContainersInvoiceWithoutC`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "User-Id": userId,
          },
        },
      );

      const createdContainers = response?.data?.containers;

      // Handle the server response
      if (response.status === 200) {
        setShippedContainers(createdContainers);
        await handleChildUploadDocs(response?.data?.invoice?.id);
        navigate("/noCustomerInvoices");
        setError("");
      }
    } catch (error) {
      const errMsg = error.response?.data?.message;
      if (errMsg === "alreadyLinkedToAnInvoice") {
        const containerNumber = error?.response?.data?.containerNumber;
        setError(`${t("container")} ${containerNumber} ${t(errMsg)}`);
      } else {
        setError(errMsg || "An error occurred");
      }
      window.scroll(0, 0);
    } finally {
      setLoadingModal(false);
    }
  };

  const handleAttemptCancel = () => {
    setShowAreYouSure(true);
  };

  const handleCancel = () => {
    setInputs([
      {
        containerNumber: "",
        expectedArrivalDate: new Date(),
        finalDestination: "",
        shipper: "",
        material: "",
        numberOfBoxes: 0,
        unitPrice: 0,
        exitDate: new Date(),
        dateOfEntry: new Date(),
        shippingDate: new Date(),
        permitsReceivingDate: new Date(),
        amountForSupplier: "",
        abstractAmountForCustomer: "",
        finalAmountForCustomer: "",
        notes: "",
      },
    ]);
    navigate("/noCustomerInvoices");
  };

  return (
    <>
      <PageWrapper className="add-shipped-container-page" withBackButton>

        <h3 className={"add-shipped-container-title"}>{t("createInvoice")}</h3>

        {loadingModal ? <Loader /> : null}
        {error && !loadingModal && <ErrorBox>{t(error)}</ErrorBox>}

        <div className={` add-shipped-container-invoice-part`}>
          {!selectedSupplier?.id || selectedSupplier?.id === "All" ? (
            <InformationBox>{t("youHaveToSelectSupplier")}</InformationBox>
          ) : null}
          <div className={classes.section}>
            {/* // "add-shipped-container-header" */}
            {/* // className="add-shipped-container-header-section" */}
            <section className={classes.inputContainer}>
              <span>{t("supplier")}: </span>
              <HotDropdown
                initOptions={suppliers}
                selected={
                  selectedSupplier.id && selectedSupplier.id !== "All"
                    ? selectedSupplier
                    : { id: "", name: `--${t("chooseASupplier")}--` }
                }
                setSelected={handleSelectSupplier}
                page="noRequest"
                getData={getSuppliers}
                errorText={error}
                optionsName={"suppliers"}
                flex={true}
              />
            </section>
          </div>

          <section className={classes.section}>
            <div className={classes.modalSubHeaderDivider}>
              <span className={classes.modalSubHeaderDividerFirstDiv}>
                {t("invoiceDetails")}:{" "}
              </span>
              <span className={classes.modalSubHeaderDividerSecondDiv}></span>
            </div>
          </section>
          <section className={classes.section}>
            <div className={classes.inputContainer}>
              <span>{t("sendingCompany")}: </span>
              <Dropdown
                options={subCompanies}
                selected={
                  selectedSubCompany !== null
                    ? selectedSubCompany
                    : { id: "", name: `--${t("chooseASubCompany")}--` }
                }
                setSelected={handleSelectSubCompany}
                flex={true}
                getData={getSubCompanies}
                nameInAr={true}

              />
            </div>
          </section>
          <section className={classes.section}>
            <div className={classes.inputContainer}>
              <span>{t("invoiceNumber")}: </span>
              <div className={classes.invoiceNumberContainer}>
                <CustomInput
                  name="invoiceNumber"
                  value={invoice?.invoiceNumber}
                  onChange={handleInvoiceInputChange}
                  flex={true}
                />
              </div>
            </div>
          </section>
          <section className={classes.section}>
            <div className={classes.inputContainer}>
              <span>{t("Date")}: </span>
              <CustomInput
                name="date"
                type="date"
                flex={true}
                value={
                  invoice?.date ? invoice?.date.toISOString().split("T")[0] : ""
                }
                onChange={(e) => handleInvoiceDateChange(e)}
              />
            </div>
          </section>
          <section className={classes.section}>
            <div className={classes.inputContainer}>
              <span>{t("amountForSupplier")}: </span>
              <div
                className={`o-row o-row--horizontal-align_space-between o-row--gap-size_400 o-row--vertical-align_center u-width_full-width`}
              >
                <div className={classes.invoiceNumberContainer}>
                  <CustomInputWithUnit
                    name="amountForSupplier"
                    value={invoice?.amountForSupplier}
                    onChange={handleAmountChange}
                    // onKeyDown={(e) => onlyNums(e)}
                    type="moneyV2"
                    flex={true}
                    unit={selectedSupplier?.currency}
                  />
                </div>
              </div>
            </div>
          </section>

          <section className={classes.section}>
            <div className={classes.inputContainer}>
              <span className={classes.notesClass}>{t("notes")}: </span>
              <div
                className={`o-row o-row--horizontal-align_space-between o-row--gap-size_400 o-row--vertical-align_center u-width_full-width`}
              >
                <div className={classes.invoiceNumberContainer}>
                  <CustomInput
                    name="notes"
                    value={invoice?.notes}
                    onChange={handleAmountChange}
                    type="textarea"
                    flex={true}
                  />
                </div>
              </div>
            </div>
          </section>

          <PDFUploader
            ref={pdfUploaderRef}
            invoiceNumber={invoice?.invoiceNumber}
            invoiceDocs={invoiceDocs}
            setInvoiceDocs={setInvoiceDocs}
            mode="upload"
            documents={invoiceDocs}
            tempDocs={invoiceDocs}
            setTempDocs={setInvoiceDocs}
            invoiceType="multiContainersInvoice"
          />
        </div>

        {inputs?.length > 0 && (
          <>
            <section className={`${classes.section} ${classes.invoiceNumber}`}>
              <div className={classes.modalSubHeaderDivider}>
                <span className={classes.modalSubHeaderDividerFirstDiv}>
                  {t("containers")}:{" "}
                </span>
                <span className={classes.modalSubHeaderDividerSecondDiv}></span>
              </div>
            </section>

            <div className="all-containers u-styled-scrollbar">
              {inputs?.map((item, i) => {
                return (
                  <ShippedContainerCard
                    key={i}
                    containerInfo={item}
                    containersLength={inputs.length}
                    index={i}
                    src={getRandomIcon()}
                    currentDirection={currentDirection}
                    withDelete={true}
                    handleAttemptDelete={(e) =>
                      handleAttemptDeleteContainer(e, i)
                    }
                    invoice={invoice}
                    setInvoice={setInvoice}
                    onContainerChange={(data) =>
                      setInputs((prev) =>
                        prev.map((item, index) => (index === i ? data : item)),
                      )
                    }
                  />
                );
              })}
            </div>
          </>
        )}

        {loadingModal ? <Loader /> : null}

        <section className="o-column o-column--vertical-align_end o-column--gap-size_500 u-margin--top_400">
          <CustomButton
            onClick={() => openModal(true)}
            variant="blank"
            title={
              inputs?.length > 1 ? t("addAnotherContainer") : t("addContainer")
            }
            icon={addIcon}
            iconSize={16}
            disabled={loadingModal}
          />
          <div className="o-row o-column--gap-size_500">
            <CustomButton
              title={t("cancel")}
              onClick={handleAttemptCancel}
              variant="blank"
              icon={closeIcon}
              iconSize={14}
            />
            <CustomButton
              onClick={handleAddRow}
              title={t("confirm")}
              icon={saveIcon}
              iconSize={16}
              disabled={
                loadingModal ||
                !selectedSupplier?.id ||
                selectedSupplier?.id === "All"
              }
            />
          </div>
        </section>
      </PageWrapper>
      {showAddShippedContainerModal && (
        <AddNoCustomerContainerModal
          inputs={inputs}
          setInputs={setInputs}
          onClose={() => setShowAddShippedContainerModal(false)}
          setShowAddShippedContainerModal={() =>
            setShowAddShippedContainerModal(false)
          }
        />
      )}

      {/* Cancel adding the Whole invoice: Are you sure modal */}
      {showAreYouSure ? (
        <AreYouSureModal
          shouldCloseModal={shouldCloseModal({
            ...invoice,
            ...inputs,
            ...invoiceDocs,
          })}
          onClose={() => setShowAreYouSure(false)}
          onConfirm={handleCancel}
          confirmIconType="whiteCheck"
        />
      ) : null}

      {/* Delete: Are you sure modal */}
      {areYouSureDelete && containerToDelete !== null ? (
        <AreYouSureModal
          customMessage={"confirmDeleteContainer"}
          closeTitle={"cancel"}
          confirmTitle={"delete"}
          onClose={() => {
            setAreYouSureDelete(false);
            setContainerToDelete(null);
          }}
          onConfirm={() => handleDeleteContainer(containerToDelete)}
          confirmIconType="whiteCheck"
        />
      ) : null}
    </>
  );
};

export default CreateNoCustomerInvoice;
